import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { NotificationAction } from 'types/notification'
import { UserDataType } from 'types/autentication'
import { notificationStatus } from 'constants/notificationStatus'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import ActionStatus from 'components/ActionStatus'
import { BsQuestionLg } from 'react-icons/bs'
import { SwitchToggle } from 'components/SwitchToggle'
import { TextArea } from 'components/TextArea'
import { useForm } from 'react-hook-form'
import { Tooltip } from 'react-tooltip'
import { damage_level, getCorIncidente, getDamage } from 'utils/damage'
import { SwitchDiv, inputRequiredStyle, MoreDetailsText } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/FinishStep/styles'
import { OutlinePersonIcon } from 'components/Icons/OutlinePersonIcon'
import { JustifyGroupTreatment } from './JustifyGroupTreatment'

export interface GroupData {
    group_key: string
    group_info: {
        u_notifica_user: { value: string; display_value: string }
        u_number: { value: string; display_value: string }
        u_related_notifications: { value: string; display_value: string }
        u_sector: { value: string; display_value: string }
        u_incident_type: { value: string; display_value: string }
        u_group_damage_rating: { value: string; display_value: string }
        u_location: { value: string; display_value: string }
        u_assigned_to: { value: string; display_value: string }
        sys_id: { value: string; display_value: string }
    }
    total_notifications: number
    notifications: any[]
}

export interface GroupFinishStepProps {
    id: string
    tooltipAlert: string
    currentUser: UserDataType
    statusIsPendingFinishOrFinished: boolean
    notificationStatusState: string
    groupData?: GroupData
    onJustificationChange?: (isFilled: boolean, value?: string) => void
    onDisclosureChange?: (disclosureData: { u_disclosure: string; u_disclosure_comments: string }) => void
    loggedInUserIsResponsible?: boolean
}

const GroupFinishStep = ({
    id,
    tooltipAlert,
    currentUser,
    statusIsPendingFinishOrFinished: statusIsArchivedOrFinished,
    notificationStatusState,
    groupData,
    onJustificationChange,
    onDisclosureChange,
    loggedInUserIsResponsible = true
}: GroupFinishStepProps) => {
    

    const {
        register,
        control,
        watch,
        setValue
    } = useForm()

    // Para tratamento em grupo, disclosure sempre será "sim" (true)
    const [enabledDisclosureComments, setEnabledDisclosureComments] = useState(true)
    const uDisclosureComments = watch('u_disclosure_comments') || ''
    const uDisclosure = watch('u_disclosure')

    // Usar primeira notificação como referência
    const firstNotification = useMemo(() => {
        return groupData?.notifications?.[0]
    }, [groupData])

    // Obter dados de disclosure da primeira notificação
    const disclosureData = useMemo(() => {
        return firstNotification?.u_disclosure
    }, [firstNotification])

    const disclosureCommentsData = useMemo(() => {
        return firstNotification?.u_disclosure_comments
    }, [firstNotification])

    // Determinar se deve mostrar disclosure baseado nas notificações do grupo
    const showDisclosure = useMemo(() => {
        if (groupData?.notifications) {
            groupData.notifications.forEach((notification, index) => {
                console.log(`Notification ${index}:`, {
                    sys_id: notification.sys_id?.value,
                    u_disclosure: notification.u_disclosure,
                    u_disclosure_comments: notification.u_disclosure_comments,
                    damage_level: notification.damage_level,
                    u_image_risk: notification.u_image_risk
                })
            })
        }
        
        // Verificar se alguma notificação do grupo tem disclosure configurado
        const hasDisclosure = groupData?.notifications?.some(notification => 
            notification.u_disclosure?.value && 
            notification.u_disclosure?.value !== 'u_disclosure_no'
        )
        
        if (hasDisclosure) {
            return true
        }
        
        if (!firstNotification?.damage_level || !firstNotification?.u_image_risk) {
            return false
        }

        const damagesLevelClosureIds = damage_level
            .filter((damage) => ['leve', 'moderado', 'grave', 'obito'].includes(damage.value))
            .map((damage) => damage.id)

        const traditionalCriteria = (
            damagesLevelClosureIds.includes(firstNotification?.damage_level?.value) ||
            firstNotification?.u_image_risk?.value !== 'u_image_risk_no' ||
            firstNotification?.u_never_events_or_sentinel_event?.value === 'yes'
        )
        
        return traditionalCriteria
    }, [firstNotification, groupData])

    // Determinar se é tratamento simplificado para o grupo
    const isSimpleGroupTreatment = useMemo(() => {
        // Por enquanto, sempre retorna true para visualização
        // A lógica completa será implementada depois
        return true
    }, [groupData, currentUser])

    const isDone = useMemo(() => {
        // Se o status já é finalizado ou pendente finalização
        if (notificationStatusState === notificationStatus[4].label || notificationStatusState === notificationStatus[5].label) {
            return true
        }
        
        return false
    }, [notificationStatusState])

    const setDisclosureEnabled = (b: boolean) => {
        // Para tratamento em grupo, sempre manter como "sim" (true)
        setEnabledDisclosureComments(true)
        setValue('u_disclosure', 'u_disclosure_yes')
    }

    const submitManagerReview = () => {
        // Handler para salvar justificativa
        // Será implementado posteriormente
    }

    useEffect(() => {
        if (!showDisclosure) return
        // Garantir configuração única por mudança de primeiro registro
        setEnabledDisclosureComments(true)
        setValue('u_disclosure', 'u_disclosure_yes')
        // Evitar sobrescrever a digitação do usuário em todo render; só inicializa quando muda a fonte
        if (disclosureCommentsData?.value) {
            setValue('u_disclosure_comments', disclosureCommentsData.value)
        }
    }, [showDisclosure, disclosureCommentsData?.value, setValue])

    // Notificar componente pai quando disclosure mudar
    useEffect(() => {
        if (!onDisclosureChange || !showDisclosure) return
        onDisclosureChange({
            u_disclosure: 'u_disclosure_yes',
            u_disclosure_comments: uDisclosureComments || ''
        })
        // Depend somente do texto que o usuário digita e do controle de exibição
    }, [uDisclosureComments, onDisclosureChange, showDisclosure])


    return (
        <div data-testid="test-group-finish-step" className="min-h-[400px]">
            <div className="px-8 flex flex-row gap-12 mt-4 ">
                <div className="flex flex-col ">
                    <>
                        <span className="text-xs text-darkGray80">
                            Responsável
                        </span>
                        <div className="flex items-center gap-2">
                            <OutlinePersonIcon
                                width="24"
                                height="24"
                                color="#893367"
                            />
                            <span className="text-base text-roxo font-semibold">
                                {groupData?.notifications?.[0]?.u_assigned_to?.display_value || 'Não informado'}
                            </span>
                        </div>
                    </>
                    <div className="flex flex-col mt-2">
                        <span className="text-xs text-darkGray80">Chave do grupo</span>
                        <span className="text-sm font-semibold text-darkGray">
                            {groupData?.group_info?.u_number?.display_value}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col">
                    <>
                        <span className="text-xs text-darkGray80">Unidade</span>
                        <span className="font-semibold text-sm text-darkGray">
                            {groupData?.group_info?.u_location?.display_value}
                        </span>
                    </>
                    <div className="flex flex-col mt-4">
                        <span className="text-xs text-darkGray80">Grau de dano do grupo</span>
                        <span className="font-semibold text-sm text-darkGray">
                            {getDamage(groupData?.group_info?.u_group_damage_rating?.value)?.label}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col">
                    <>
                        <span className="text-xs text-darkGray80">Setor</span>
                        <span className="font-semibold text-sm text-darkGray">
                            {groupData?.group_info?.u_sector?.display_value}
                        </span>
                    </>
                    <div className="flex flex-col mt-4">
                        <span className="text-xs text-darkGray80">Tipo do incidente do grupo</span>
                        <span className="font-semibold text-sm text-darkGray">
                            {groupData?.group_info?.u_incident_type?.display_value}
                        </span>
                    </div>
                </div>
            </div>
            <div className="h-px my-2 bg-gray-200 mx-8 mt-4" />

            {/* Seção de Notificações do grupo */}
            <div className="px-8 mt-4">
                <div className="flex items-center space-x-4 mb-4">
                    <h2 className="text-lg font-semibold text-blueTheme">Notificações do grupo</h2>
                    <span className="bg-[#D9D9D9] text-gray-700 text-sm px-2.5 py-0.5 rounded-full">
                        {groupData?.total_notifications || 0} {groupData?.total_notifications === 1 ? 'notificação' : 'notificações'}
                    </span>
                </div>
            </div>

{/* Informações de tratamento e finalização */}
{isDone && (
                <div className="px-8 mt-4">
                    <div className="flex flex-row gap-12">
                        <div className="flex flex-col">
                            <span className="text-xs text-darkGray80">Tratada por</span>
                            <span className="text-sm font-semibold text-darkGray">
                                {groupData?.notifications?.[0]?.u_assigned_to?.display_value || 'Não informado'}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-darkGray80">Finalizada por</span>
                            <span className="text-sm font-semibold text-darkGray">
                                {firstNotification?.u_assigned_to?.display_value || currentUser?.name}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            
            {showDisclosure && (
                <div className="px-8">
                    <div className="w-full mt-3">
                        <div className="z-20">
                            {/* Para tratamento em grupo, não mostrar switch - disclosure sempre será "sim" */}
                            {(enabledDisclosureComments &&
                                (!isDone ?
                                    (<TextArea
                                        container="mt-[28px] min-w-full max-w-full w-full"
                                        label="Justificativa da finalização"
                                        subLabel="Obrigatório o parecer do responsável para finalizar o tratamento em grupo."
                                        name="u_disclosure_comments"
                                        register={register}
                                        className={`max-h-[257px] h-[257px]`}
                                        placeholder="Digite aqui"
                                        inputLength={2000 - uDisclosureComments.length}
                                        maxLength={2000}
                                        disabled={(() => {
                                            const isLocationManagerBlocked = (['location_manager',].includes(currentUser?.profile) && ['4'].includes(notificationStatusState))
                                            // corporate_quality_board NÃO pode editar notificações em grupo
                                            const isProfileBlocked = ['nsp', 'unit_general_manager', 'corporate_quality_management', 'corporate_quality_board'].includes(currentUser?.profile)
                                            const isDoneBlocked = isDone
                                            const isNotResponsibleBlocked = !loggedInUserIsResponsible
                                            
                                            return isLocationManagerBlocked || isProfileBlocked || isDoneBlocked || isNotResponsibleBlocked
                                        })()}
                                        rows={10}
                                    />)
                                    :
                                    (<>
                                        <div className="flex flex-col mt-[28px] mb-[2px] min-w-full max-w-full w-full">
                                            <span className="text-sm font-semibold text-darkGray">Justificativa da finalização</span>
                                            <span className="text-xs font-normal outline-gray-200 rounded-lg text-darkGray ring-0 min-h-[257px]">{disclosureCommentsData?.display_value || uDisclosureComments}</span>
                                        </div>
                                    </>))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Removido o alerta local para evitar render duplo e posicionamento incorreto; o alerta é exibido no topo do step 1 */}
            

            <JustifyGroupTreatment
                managerReview={firstNotification?.u_managers_review?.display_value}
                isDone={isDone}
                isSimpleTreatment={isSimpleGroupTreatment && !showDisclosure}
                register={register}
                submitManagerReview={submitManagerReview}
                managersReviewError=""
                totalNotifications={groupData?.total_notifications || 0}
                onJustificationChange={onJustificationChange}
                loggedInUserIsResponsible={loggedInUserIsResponsible}
            />
        </div>
    )
}

export { GroupFinishStep }
